import React, { useState, useEffect } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  useMediaQuery,
} from "@mui/material";
import Header from "../components/Header";
import FooterComponent from "../components/Footer";
import apis from "../utils/apis";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";

const ResumeRanker = () => {
  const [jdFile, setJdFile] = useState(null); // For uploaded JD file
  const [jdText, setJdText] = useState(""); // For manual JD text input
  const [resumeZip, setResumeZip] = useState(null); // For uploaded resumes
  const [loading, setLoading] = useState(false); // For API call status
  const [rows, setRows] = useState([]); // Data for the table
  const [error, setError] = useState(""); // Error messages
  const [jdInputType, setJdInputType] = useState("file"); // JD input type: file or text
  const [jobDescriptionContent, setJobDescriptionContent] = useState(""); // For displaying JD content from URL
  const isMobile = useMediaQuery("(max-width:600px)");

  // Function to fetch Job Description from URL
  const fetchJobDescription = async (url) => {
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "text/plain",
        },
      });
      setJobDescriptionContent(response.data);
    } catch (err) {
      console.error("Failed to load Job Description from URL:", err);
      if (err.code === "ERR_NETWORK") {
        setJobDescriptionContent(
          "Network error occurred while loading Job Description."
        );
      } else {
        setJobDescriptionContent("Failed to load Job Description.");
      }
    }
  };

  // Handle file and text input changes
  const handleJdFileChange = (e) => setJdFile(e.target.files[0]);
  const handleJdTextChange = (e) => setJdText(e.target.value);
  const handleResumeChange = (e) => setResumeZip(e.target.files[0]);

  // Handle JD input type selection
  const handleJdInputTypeChange = (e) => {
    setJdInputType(e.target.value);
    setJdFile(null);
    setJdText("");
  };

  const secretEncodeDecodeString = useFeatureFlag("secretEncodeDecodeString");
  const staticRandomEncodeDecodeString = useFeatureFlag(
    "staticRandomEncodeDecodeString"
  );

  function DataDecryption(jsonString) {
    const decodedString = atob(jsonString);
    const getOldStringOne = decodedString.replace(
      new RegExp(secretEncodeDecodeString, "g"),
      ""
    );
    const str = staticRandomEncodeDecodeString;
    const escapedString = str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedString, "g");
    const getOldStringTwo = getOldStringOne.replace(regex, '"');
    const json = JSON.parse(getOldStringTwo);
    return json;
  }

  // Convert file to Base64
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]); // Remove Base64 header
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Ensure either JD file or JD text is provided, but not both
    if (
      (jdInputType === "file" && !jdFile) ||
      (jdInputType === "text" && !jdText) ||
      !resumeZip
    ) {
      setError("Please provide either a JD file or JD text, and resumes.");
      return;
    }

    if (jdInputType === "file" && jdFile) {
      const allowedExtensions = [".pdf", ".txt", ".docx"];
      const fileExtension = jdFile.name.split(".").pop().toLowerCase();
      if (!allowedExtensions.includes(`.${fileExtension}`)) {
        setError("JD file must be a .pdf, .txt, or .docx file.");
        return;
      }
    }

    // Clear old data and reset state
    setRows([]);
    setError("");
    setLoading(true);

    try {
      const formData = {};
      setError("Uploading Job Description and resumes...");

      // Prepare JD input
      if (jdInputType === "file") {
        formData.jobDescriptionFile = await fileToBase64(jdFile);
      } else {
        formData.jobDescription = jdText;
      }

      // Prepare resumes input
      formData.zipFileContent = await fileToBase64(resumeZip);

      // Step 1: Call `/upload-jd-resume` API
      setError("Uploading files to the server...");
      const uploadResponse = await apis.POST({
        endpoint: "/upload-jd-resume",
        payload: formData,
        options: { type: "json" },
      });

      const { jobDescriptionUrl, jdCriteria, fileUrls } = uploadResponse.data;

      // Validate response
      if (
        !jobDescriptionUrl ||
        !jdCriteria ||
        !fileUrls ||
        fileUrls.length === 0
      ) {
        console.log(uploadResponse.data);
        setError(
          uploadResponse.data.error || "Invalid response from the server."
        );
        return;
      }

      // Fetch Job Description content from URL
      fetchJobDescription(jobDescriptionUrl);

      setError("Processing resumes...");
      const processedResumes = [];

      // Step 2: Process each resume using `/process-single-resume` API
      for (const fileUrl of fileUrls) {
        try {
          const fileName = fileUrl.split("/").pop().split("%")[0]; // Extract first word of file name before "%"
          setError(`Processing resume: ${fileName}...`);
          const processResponse = await apis.POST({
            endpoint: "/process-single-resume",
            payload: { fileUrl },
            options: { type: "json" },
          });
          processedResumes.push(processResponse.data);
        } catch (err) {
          console.error(`Error processing resume: ${fileUrl}`, err);
          const fileName = fileUrl.split("/").pop().split("%")[0]; // Extract first word of file name before "%"
          setError(`Failed to process resume: ${fileName}`);
          return;
        }
      }

      setError("Ranking resumes...");
      // Step 3: Call `/jd-to-resume-ranking` API
      const rankingResponse = await apis.POST({
        endpoint: "/jd-to-resume-ranking",
        payload: { jdCriteria, resumes: processedResumes },
        options: { type: "json" },
      });

      const parsedData = rankingResponse.data;
      if (parsedData && Array.isArray(parsedData)) {
        // Transform data to include unique IDs and map skills into a single `skills` field
        const rowsWithIdsAndSkills = parsedData.map((row, index) => ({
          id: index, // Use the index as a unique identifier
          Fit: row.Fit,
          FitPercentage: row.FitPercentage,
          Name: row.Name,
          Location: row.Location,
          Experience: row.Experience,
          Role: row.Role,
          Skills: row.Skills.map((skill) => skill.name).join(", "), // Combine skills into a single string
          Certification: row.Certification,
        }));

        setRows(rowsWithIdsAndSkills); // Set the transformed rows
        setError(""); // Clear error message
      } else {
        setError("No data received from the server.");
      }
    } catch (err) {
      console.log(err);
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error); // Display error message from the server
      } else {
        setError("An error occurred while processing. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Table columns
  const columns = [
    { field: "Fit", headerName: "Fit", width: isMobile ? 100 : 150 },
    {
      field: "FitPercentage",
      headerName: "Fit %",
      width: isMobile ? 100 : 150,
    },
    { field: "Name", headerName: "Name", width: isMobile ? 150 : 200 },
    { field: "Location", headerName: "Location", width: isMobile ? 150 : 200 },
    {
      field: "Experience",
      headerName: "Experience",
      width: isMobile ? 150 : 200,
    },
    { field: "Role", headerName: "Role", width: isMobile ? 150 : 200 },
    { field: "Skills", headerName: "Skills", width: isMobile ? 200 : 500 },
    {
      field: "Certification",
      headerName: "Certification",
      width: isMobile ? 150 : 200,
    },
  ];

  return (
    <Box sx={{ mt: 9 }}>
      <Box
        sx={{
          padding: 6,
          display: "grid",
          placeItems: "center",
          height: "100vh",
        }}
      >
        <Header />

        {/* File Upload Form */}
        <form onSubmit={handleSubmit} style={{ marginBottom: 20 }}>
          {/* JD Input Type Selection */}
          <Typography variant="h6" gutterBottom>
            Choose how to provide the Job Description (JD):
          </Typography>
          <RadioGroup
            row
            value={jdInputType}
            onChange={handleJdInputTypeChange}
            style={{ marginBottom: 20 }}
          >
            <FormControlLabel
              value="file"
              control={<Radio />}
              label="Upload JD File"
            />
            <FormControlLabel
              value="text"
              control={<Radio />}
              label="Enter JD Text"
            />
          </RadioGroup>

          {/* JD Input */}
          {jdInputType === "file" && (
            <div style={{ marginBottom: 10 }}>
              <label>
                <strong>Upload Job Description (JD):</strong>
              </label>
              <input
                type="file"
                accept=".pdf,.docx,.txt"
                onChange={handleJdFileChange}
                style={{ marginLeft: 10 }}
              />
            </div>
          )}
          {jdInputType === "text" && (
            <div style={{ marginBottom: 10 }}>
              <TextField
                label="Enter Job Description (JD)"
                multiline
                rows={4}
                fullWidth
                value={jdText}
                onChange={handleJdTextChange}
                variant="outlined"
              />
            </div>
          )}

          {/* Resume Upload */}
          <div style={{ marginBottom: 10 }}>
            <label>
              <strong>Upload Resumes (.zip):</strong>
            </label>
            <input
              type="file"
              accept=".zip"
              onChange={handleResumeChange}
              style={{ marginLeft: 10 }}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? "Processing..." : "Process Files"}
          </Button>
        </form>

        {/* Loading Indicator */}
        {loading && <CircularProgress style={{ marginTop: 20 }} />}

        {/* Error Message */}
        {error && (
          <Typography color="error" style={{ marginTop: 20 }}>
            {error}
          </Typography>
        )}

        {/* Job Description Content */}
        {jobDescriptionContent && (
          <Box sx={{ width: "70%", marginTop: 20 }}>
            <Typography variant="h5" gutterBottom>
              Job Description
            </Typography>
            <Typography>{jobDescriptionContent}</Typography>
          </Box>
        )}

        {/* Data Table */}
        {rows.length > 0 && (
          <Box
            sx={{
              height: isMobile ? 300 : 400,
              width: isMobile ? "100%" : "70%",
              marginTop: 20,
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={isMobile ? 5 : 10}
              rowsPerPageOptions={isMobile ? [5] : [10]}
              getRowId={(row) => row.id} // Ensure unique ID
            />
          </Box>
        )}
        <Box sx={{ pt: "25%" }}>
          <FooterComponent />
        </Box>
      </Box>
    </Box>
  );
};

export default ResumeRanker;
